import { createRng, type Rng } from '../rng/seededRandom';
import type { DuelLevel } from '../battle/aiSkill';
import { effectiveHitPoints } from './battle';
import { cardExpertRating, cardLogit, cardPairModel, withCardEvaluation } from './cardPower';
import { pieceById } from './movement';
import type {
  BattleResult,
  CardFactionState,
  CardMatchState,
  CardRole,
  ElementCard,
  FactionId,
  GameContext,
  GameState,
  Piece,
  SupportCardId,
} from './types';

export const CARD_HAND_START = 4;
export const CARD_HAND_MAX = 5;
export const CARD_SUPPORT_CAP = 4;
export const ELEMENT_CARDS: readonly ElementCard[] = ['earth', 'water', 'air', 'fire'];
/** How long a person has to commit a support, in a game at one table and in a hosted one. */
export const CARD_CHOICE_SECONDS = 15;
/**
 * How long the revealed cards wait online for the people in the clash to read them before the
 * game goes on without their Continue: long enough to watch the clash told and read its result,
 * short enough that one idle player cannot hold the others up for ever. At one table they wait
 * for Continue as long as it takes; a clash between AI sides passes in a beat.
 */
export const CARD_REVEAL_SECONDS = 30;

export interface CardChoice {
  cardId: SupportCardId | null;
  /** Required only for Water; other cards have an authored role. */
  role?: CardRole;
}

export interface CardScore {
  faction: FactionId;
  pieceId: number;
  main: number;
  support: number;
  total: number;
  cardId: SupportCardId | null;
  role: CardRole | null;
  fit: boolean;
  counter: boolean;
  probability?: number;
  source?: 'measured' | 'estimated';
  won?: boolean;
  level?: DuelLevel;
}

export interface CardResolution {
  result: BattleResult;
  rng: number;
  scores: CardScore[];
  winnerFaction: FactionId;
  cards: CardMatchState;
}

const ROLE_BY_CHARACTER: Record<string, CardRole> = {
  knight: 'protection',
  goblin: 'deception',
  archer: 'pressure',
  manticore: 'pressure',
  valkyrie: 'protection',
  banshee: 'deception',
  golem: 'protection',
  troll: 'protection',
  unicorn: 'deception',
  basilisk: 'pressure',
  djinni: 'deception',
  shapeshifter: 'deception',
  phoenix: 'protection',
  dragon: 'pressure',
  wizard: 'deception',
  sorceress: 'pressure',
};
const ELEMENT_ROLE: Record<Exclude<ElementCard, 'water'>, CardRole> = {
  earth: 'protection',
  fire: 'pressure',
  air: 'deception',
};

export function supportCardName(ctx: GameContext, id: SupportCardId): string {
  const [kind, value] = id.split(':') as ['character' | 'element', string];
  return kind === 'character'
    ? (ctx.roster[value]?.name ?? value)
    : `${value[0]!.toUpperCase()}${value.slice(1)} Elemental`;
}

export function supportRole(id: SupportCardId, chosen?: CardRole, ctx?: GameContext): CardRole {
  const [kind, value] = id.split(':') as ['character' | 'element', string];
  if (kind === 'element')
    return value === 'water'
      ? (chosen ?? 'deception')
      : ELEMENT_ROLE[value as Exclude<ElementCard, 'water'>];
  return ctx?.definition?.cardSupports?.[value]?.role ?? ROLE_BY_CHARACTER[value] ?? 'deception';
}

export function supportFitDescription(id: SupportCardId, ctx?: GameContext): string {
  const key = id.split(':')[1]!;
  const descriptions: Record<string, string> = {
    knight: 'Fit: your main uses melee',
    goblin: 'Fit: your main uses melee',
    archer: 'Fit: your main attacks at range',
    manticore: 'Fit: your main uses venom',
    valkyrie: 'Fit: your main uses frost',
    banshee: 'Fit: your main is a spellcaster',
    golem: 'Fit: your main has at least 35 base health',
    troll: 'Fit: your main is already wounded',
    unicorn: 'Fit: your main uses arcane damage',
    basilisk: 'Fit: your main uses venom',
    djinni: 'Fit: your main does not teleport',
    shapeshifter: 'Fit: your main is a mimic',
    phoenix: 'Fit: your main is already wounded',
    dragon: 'Fit: your main uses fire',
    wizard: 'Fit: your main is a spellcaster',
    sorceress: 'Fit: your main uses frost',
    earth: 'Fit: your main has strong protection',
    fire: 'Fit: your main uses fire',
    air: 'Fit: your main has standard speed or faster',
    water: 'Flexible role; no fit point',
  };
  const fit = ctx?.definition?.cardSupports?.[key]?.fit;
  const authored: Record<string, string> = {
    melee: 'Fit: your main uses melee',
    wounded: 'Fit: your main is already wounded',
    ranged: 'Fit: your main attacks at range',
    venom: 'Fit: your main uses venom',
    frost: 'Fit: your main uses frost',
    spellcaster: 'Fit: your main is a spellcaster',
    sturdy: 'Fit: your main has at least 35 base health',
    arcane: 'Fit: your main uses arcane damage',
    grounded: 'Fit: your main does not teleport',
    mimic: 'Fit: your main is a mimic',
    fire: 'Fit: your main uses fire',
  };
  return (
    (fit && authored[fit]) ??
    descriptions[key] ??
    'Fit: your main shares this support’s combat style'
  );
}

function shuffle<T>(items: T[], rng: Rng): T[] {
  for (let i = items.length - 1; i > 0; i--) {
    const j = rng.int(i + 1);
    [items[i], items[j]] = [items[j]!, items[i]!];
  }
  return items;
}

function ownedCards(state: GameState, faction: FactionId): SupportCardId[] {
  const characterIds = [
    ...new Set(
      state.pieces
        .filter((p) => p.faction === faction && !p.character.startsWith('elemental-'))
        .map((p) => `character:${p.character}` as SupportCardId),
    ),
  ];
  return [...characterIds, ...ELEMENT_CARDS.map((e) => `element:${e}` as SupportCardId)];
}

/** Creates the four-card private hands once. Calling it again is a no-op. */
export function initializeCards(ctx: GameContext, state: GameState): GameState {
  if (state.cards) return state;
  const rng = createRng(state.rng);
  const factions: Record<FactionId, CardFactionState> = {};
  for (const faction of state.factions.filter((f) => !f.eliminated)) {
    const pool = shuffle(ownedCards(state, faction.id), rng);
    factions[faction.id] = { hand: pool.splice(0, CARD_HAND_START), reserve: pool, cooldown: {} };
  }
  return { ...state, rng: rng.state(), cards: { round: state.round, factions } };
}

/** Reconciles deaths/revival and performs at most one replenishment per completed global round. */
export function replenishCards(ctx: GameContext, state: GameState): GameState {
  if (!state.cards || state.cards.round >= state.round) return state;
  const rng = createRng(state.rng);
  const factions: CardMatchState['factions'] = {};
  for (const faction of state.factions.filter((f) => !f.eliminated)) {
    const previous = state.cards.factions[faction.id] ?? { hand: [], reserve: [], cooldown: {} };
    const owned = new Set(ownedCards(state, faction.id));
    const cooldown = Object.fromEntries(
      Object.entries(previous.cooldown).filter(
        ([id, round]) => owned.has(id as SupportCardId) && round! > state.round,
      ),
    ) as CardFactionState['cooldown'];
    const hand = previous.hand.filter((id) => owned.has(id));
    const invalidated = previous.hand.length - hand.length;
    const used = new Set([...hand, ...Object.keys(cooldown)]);
    const reserve = previous.reserve.filter((id) => owned.has(id) && !used.has(id));
    for (const id of owned) if (!used.has(id) && !reserve.includes(id)) reserve.push(id);
    shuffle(reserve, rng);
    for (
      let replacement = 0;
      replacement < invalidated && hand.length < CARD_HAND_MAX && reserve.length;
      replacement++
    )
      hand.push(reserve.shift()!);
    if (hand.length < CARD_HAND_MAX && reserve.length) hand.push(reserve.shift()!);
    factions[faction.id] = { hand, reserve, cooldown };
  }
  return { ...state, rng: rng.state(), cards: { round: state.round, factions } };
}

export function eligibleSupport(
  ctx: GameContext,
  state: GameState,
  main: Piece,
  cardId: SupportCardId,
): boolean {
  const cards = state.cards?.factions[main.faction];
  if (!cards?.hand.includes(cardId)) return false;
  return canProvideSupport(state, main, cardId);
}

function canProvideSupport(state: GameState, main: Piece, cardId: SupportCardId): boolean {
  if (cardId.startsWith('element:')) return main.character !== `elemental-${cardId.slice(8)}`;
  const character = cardId.slice('character:'.length);
  return state.pieces.some(
    (p) =>
      p.faction === main.faction &&
      p.character === character &&
      p.id !== main.id &&
      !p.imprisoned &&
      p.frozen === 0,
  );
}

/** Printed Expert ratings are independent of the current opponent, wounds and difficulty. */
export function cardMainScores(ctx: GameContext, state: GameState): [number, number] {
  const pending = state.pendingBattle;
  if (!pending) return [17, 17];
  const scores = cardBattleMainScores(ctx, state, [pending.attackerId, pending.defenderId]);
  return [scores[0]!, scores[1]!];
}

export function cardBattleMainScores(ctx: GameContext, state: GameState, ids?: number[]): number[] {
  ctx = withCardEvaluation(ctx);
  const pending = state.pendingBattle;
  const participants =
    ids ?? pending?.participantIds ?? (pending ? [pending.attackerId, pending.defenderId] : []);
  return participants.map((id) => cardExpertRating(ctx, pieceById(state, id)!.character));
}

function fitCondition(
  ctx: GameContext,
  state: GameState,
  main: Piece,
  cardId: SupportCardId,
): boolean {
  if (cardId === 'element:water') return false;
  if (cardId === 'element:earth')
    return (ctx.roster[main.character]?.combat?.protection ?? 0) > 0.08;
  if (cardId === 'element:fire')
    return (ctx.roster[main.character]?.attack.damageType ?? 'physical') === 'fire';
  if (cardId === 'element:air') return (ctx.roster[main.character]?.combat?.speed ?? 1) >= 1;
  const supporter = ctx.roster[cardId.slice('character:'.length)];
  if (!supporter) return false;
  const mainCharacter = ctx.roster[main.character];
  const key = supporter.id;
  const authoredFit = ctx.definition?.cardSupports?.[key]?.fit;
  if (authoredFit === 'melee') return mainCharacter?.attack.type === 'melee';
  if (authoredFit === 'wounded') return main.damage > 0;
  if (authoredFit === 'ranged') return mainCharacter?.attack.type === 'projectile';
  if (authoredFit === 'venom') return mainCharacter?.attack.damageType === 'venom';
  if (authoredFit === 'frost') return mainCharacter?.attack.damageType === 'frost';
  if (authoredFit === 'spellcaster') return mainCharacter?.spellcaster === true;
  if (authoredFit === 'sturdy') return (mainCharacter?.hitPoints ?? 0) >= 35;
  if (authoredFit === 'arcane') return mainCharacter?.attack.damageType === 'arcane';
  if (authoredFit === 'grounded') return mainCharacter?.movement.type !== 'teleport';
  if (authoredFit === 'mimic') return mainCharacter?.attack.type === 'mimic';
  if (authoredFit === 'fire') return mainCharacter?.attack.damageType === 'fire';
  if (key === 'knight') return mainCharacter?.attack.type === 'melee';
  if (key === 'goblin') return mainCharacter?.attack.type === 'melee';
  if (key === 'troll' || key === 'phoenix') return main.damage > 0;
  if (key === 'archer') return mainCharacter?.attack.type === 'projectile';
  if (key === 'manticore' || key === 'basilisk')
    return mainCharacter?.attack.damageType === 'venom';
  if (key === 'valkyrie' || key === 'sorceress')
    return mainCharacter?.attack.damageType === 'frost';
  if (key === 'banshee' || key === 'wizard') return mainCharacter?.spellcaster === true;
  if (key === 'golem') return (mainCharacter?.hitPoints ?? 0) >= 35;
  if (key === 'unicorn') return mainCharacter?.attack.damageType === 'arcane';
  if (key === 'djinni') return mainCharacter?.movement.type !== 'teleport';
  if (key === 'shapeshifter') return mainCharacter?.attack.type === 'mimic';
  if (key === 'dragon') return mainCharacter?.attack.damageType === 'fire';
  const role = supportRole(cardId, undefined, ctx);
  if (role === 'pressure') return supporter.attack.type !== 'melee';
  if (role === 'protection') return supporter.hitPoints >= mainHp(ctx, state, main);
  return supporter.movement.type !== 'ground' || supporter.spellcaster === true;
}

function mainHp(ctx: GameContext, state: GameState, piece: Piece): number {
  const pending = state.pendingBattle!;
  return effectiveHitPoints(ctx, state, piece, pending.x, pending.y);
}

const counters = (mine: CardRole, theirs: CardRole | null) =>
  (mine === 'pressure' && theirs === 'deception') ||
  (mine === 'protection' && theirs === 'pressure') ||
  (mine === 'deception' && theirs === 'protection');

/** A support point multiplies odds by 1.2, not win probability by 1.2. */
export const CARD_SUPPORT_LOG_ODDS = Math.log(1.2);
export const cardSupportLogOdds = (ctx: GameContext): number =>
  (CARD_SUPPORT_LOG_ODDS * 0.5) / (ctx.definition?.cardScoreScale ?? 0.5);

function prepareCardBattle(ctx: GameContext, state: GameState) {
  ctx = withCardEvaluation(ctx);
  const pending = state.pendingBattle;
  if (!pending) throw new Error('No battle to resolve');
  const mains = (pending.participantIds ?? [pending.attackerId, pending.defenderId])
    .map((id) => pieceById(state, id))
    .filter((p): p is Piece => !!p);
  if (mains.length < 2) throw new Error('Battle participant is missing');
  const models = mains.map((a) =>
    mains.map((d) =>
      a.id === d.id
        ? { probability: 0.5, source: 'measured' as const }
        : cardPairModel(ctx, state, a, d),
    ),
  );
  // Division by N preserves exact two-way odds, without amplifying six-way advantages.
  const utilities = models.map(
    (row) => row.reduce((sum, p) => sum + cardLogit(p.probability), 0) / mains.length,
  );
  return { mains, utilities, models, ctx };
}

/** Pure probabilities shared by resolution and AI; no random outcome is inspected by the AI. */
export function cardBattleChances(
  ctx: GameContext,
  state: GameState,
  choices: Partial<Record<FactionId, CardChoice>>,
  prepared = prepareCardBattle(ctx, state),
): CardScore[] {
  const { mains, utilities, models } = prepared;
  const roles = mains.map((main) => {
    const choice = choices[main.faction];
    return choice?.cardId && eligibleSupport(ctx, state, main, choice.cardId)
      ? supportRole(choice.cardId, choice.role, ctx)
      : null;
  });
  const primaryThreat = mains.map((_, i) =>
    mains
      .map((piece, j) => ({ piece, j }))
      .filter(({ j }) => j !== i)
      .filter(
        ({ j }) =>
          Math.abs(utilities[j]! - Math.max(...utilities.filter((_, k) => k !== i))) < 1e-12,
      ),
  );
  const scores = mains.map((main, i): CardScore => {
    const choice = choices[main.faction];
    const cardId =
      choice?.cardId && eligibleSupport(ctx, state, main, choice.cardId) ? choice.cardId : null;
    const role = roles[i]!;
    const providerCount = cardId?.startsWith('character:')
      ? state.pieces.filter(
          (p) =>
            p.faction === main.faction &&
            p.character === cardId.slice(10) &&
            p.id !== main.id &&
            !p.imprisoned &&
            p.frozen === 0,
        ).length
      : 0;
    const base = cardId ? (providerCount >= 2 ? 2 : 1) : 0;
    const fit = cardId ? fitCondition(ctx, state, main, cardId) : false;
    const threats = primaryThreat[i]!;
    const counterShare = role
      ? threats.filter((t) => counters(role, roles[t.j]!)).length / threats.length
      : 0;
    const counter = counterShare > 0;
    const support = Math.min(CARD_SUPPORT_CAP, base + (fit ? 1 : 0) + 2 * counterShare);
    return {
      faction: main.faction,
      pieceId: main.id,
      main: 0,
      support,
      total: 0,
      cardId,
      role,
      fit,
      counter,
      source: models[i]!.every((m) => m.source === 'measured') ? 'measured' : 'estimated',
      level: ctx.cardLevels?.[main.faction] ?? 'expert',
    };
  });
  const weights = utilities.map((u, i) => u + cardSupportLogOdds(ctx) * scores[i]!.support);
  const max = Math.max(...weights),
    exp = weights.map((w) => Math.exp(w - max));
  const sum = exp.reduce((a, b) => a + b, 0);
  return scores.map((score, i) => ({ ...score, probability: exp[i]! / sum }));
}

/**
 * The wounds a clash's winner carries back to the board, as Roll's (`resolveRollBattle`): the
 * loser's strength over its own, at most one, of its health, times `0.3 + 0.5·u` for a draw `u` in
 * [0, 1), so 0.55 of it on average, which the board AI predicts (`predictedWinnerDamage`). Card once
 * took them from an Arena survivor fit, fitted before phases B and C, whose attack-type terms spared
 * Light's winners about six points of health a win more than Dark's (THE_DECISION_TABLE #63).
 */
function clashWounds(
  ctx: GameContext,
  state: GameState,
  winner: Piece,
  loser: Piece,
  u: number,
): number {
  const hp = mainHp(ctx, state, winner);
  const closeness = Math.min(
    1,
    (mainHp(ctx, state, loser) * (ctx.roster[loser.character]?.attack.damage ?? 1)) /
      (hp * (ctx.roster[winner.character]?.attack.damage ?? 1)),
  );
  return Math.min(hp - 1, Math.round(hp * closeness * (0.3 + 0.5 * u)));
}

/**
 * The number in [0, 1) that decides a clash: laid along the participants' chances in piece-id
 * order, it falls in the winner's. What `resolveCardBattle` draws, so the clash can be shown
 * being decided.
 */
export function cardDraw(ctx: GameContext, input: GameState): number {
  return createRng(initializeCards(ctx, input).rng).next();
}

export function resolveCardBattle(
  ctx: GameContext,
  input: GameState,
  choices: Partial<Record<FactionId, CardChoice>>,
): CardResolution {
  const state = initializeCards(ctx, input);
  const prepared = prepareCardBattle(ctx, state);
  const scores = cardBattleChances(ctx, state, choices, prepared).map((score) => {
    const main = cardExpertRating(prepared.ctx, pieceById(state, score.pieceId)!.character);
    return { ...score, main, total: main + score.support };
  });
  const rng = createRng(state.rng);
  const ordered = [...scores].sort((a, b) => a.pieceId - b.pieceId);
  let draw = rng.next();
  const winnerScore = ordered.find((score) => (draw -= score.probability!) < 0) ?? ordered.at(-1)!;
  const loserScore = scores
    .filter((s) => s !== winnerScore)
    .sort((a, b) => b.probability! - a.probability! || a.pieceId - b.pieceId)[0]!;
  const winner = prepared.mains.find((p) => p.id === winnerScore.pieceId)!;
  const winnerDamage =
    winner.damage +
    clashWounds(ctx, state, winner, pieceById(state, loserScore.pieceId)!, rng.next());
  for (const score of scores) score.won = score.pieceId === winner.id;
  const nextFactions = spentHands(state, scores);
  return {
    result: { winnerId: winner.id, winnerDamage },
    rng: rng.state(),
    scores,
    winnerFaction: winner.faction,
    cards: { round: state.cards!.round, factions: nextFactions },
  };
}

/** The hands after a clash: each support played leaves its hand and cools down for two rounds. */
function spentHands(
  state: GameState,
  scores: readonly Pick<CardScore, 'faction' | 'cardId'>[],
): CardMatchState['factions'] {
  const factions = structuredClone(state.cards!.factions);
  for (const score of scores) {
    if (!score.cardId) continue;
    const deck = factions[score.faction];
    if (!deck) continue;
    deck.hand = deck.hand.filter((id) => id !== score.cardId);
    deck.cooldown[score.cardId] = state.round + 2;
  }
  return factions;
}

/**
 * The game as a card clash left it before its result is applied: the hands dealt if this was the
 * first clash, the supports played spent, and the generator where the clash left it (`rng`). What
 * `resolveCardBattle` hands back, rebuilt from the clash's recorded scores, for whoever replays a
 * Card game from its history (a server carrying a game on in a new process).
 */
export function spendSupports(
  ctx: GameContext,
  input: GameState,
  scores: readonly Pick<CardScore, 'faction' | 'cardId'>[],
  rng: number,
): GameState {
  const state = initializeCards(ctx, input);
  return {
    ...state,
    rng,
    cards: { round: state.cards!.round, factions: spentHands(state, scores) },
  };
}

/** Hidden-choice policy: optimize probabilities, never the seeded future result. */
export function chooseCardSupport(
  ctx: GameContext,
  state: GameState,
  main: Piece,
  level: DuelLevel = 'fair',
): CardChoice {
  const hand = state.cards?.factions[main.faction]?.hand ?? [];
  const legal = hand.filter((id) => eligibleSupport(ctx, state, main, id));
  if (!legal.length) return { cardId: null };
  const actions = choicesFor(legal);
  if (level === 'novice')
    return actions[createRng(state.rng ^ Math.imul(main.id, 0x45d9f3b)).int(actions.length)]!;
  const pending = state.pendingBattle!;
  const ids = pending.participantIds ?? [pending.attackerId, pending.defenderId];
  const mains = ids.map((id) => pieceById(state, id)!).filter(Boolean);
  const prepared = prepareCardBattle(ctx, state);
  const enemies = mains.filter((p) => p.id !== main.id).sort((a, b) => a.id - b.id);
  // Evaluate every publicly possible response as legal. Reusing the real private hand here would
  // leak it indirectly: hypothetical cards outside that hand would score as no support.
  const evaluationState = {
    ...state,
    cards: { ...state.cards!, factions: { ...state.cards!.factions } },
  };
  const responses = enemies.map((enemy) => {
    const hand = ownedCards(state, enemy.faction).filter(
      (id) =>
        !state.cards?.factions[enemy.faction]?.cooldown[id] && canProvideSupport(state, enemy, id),
    );
    evaluationState.cards.factions[enemy.faction] = {
      ...state.cards!.factions[enemy.faction]!,
      hand,
    };
    return choicesFor(hand);
  });
  const sampleRng = createRng(state.rng ^ Math.imul(main.id, 0x27d4eb2d));
  // Multi-party equilibrium is not a two-player zero-sum game. Use bounded joint public
  // response scenarios, accounting for every opponent rather than only the largest one.
  const opposing: Partial<Record<FactionId, CardChoice>>[] =
    enemies.length === 1
      ? responses[0]!.map((choice) => ({ [enemies[0]!.faction]: choice }))
      : Array.from({ length: level === 'master' ? 32 : level === 'expert' ? 24 : 12 }, () =>
          Object.fromEntries(
            enemies.map((enemy, i) => [
              enemy.faction,
              responses[i]![sampleRng.int(responses[i]!.length)]!,
            ]),
          ),
        );
  const payoff = actions.map((mine) =>
    opposing.map((theirs) => {
      const result = cardBattleChances(
        ctx,
        evaluationState,
        {
          ...theirs,
          [main.faction]: mine,
        },
        prepared,
      );
      const success = result.find((score) => score.pieceId === main.id)!.probability!;
      // Preserve a card when it cannot alter the outcome; margin alone wasted the best supports.
      return success - (mine.cardId ? 0.025 : 0);
    }),
  );
  if (level === 'fair') {
    const averages = payoff.map((row) => row.reduce((sum, value) => sum + value, 0) / row.length);
    return actions[indexOfMax(averages)]!;
  }
  // Pure maximin degenerates to passing when each support has some counter. Simultaneous
  // hidden choices need a mixed strategy at both advanced levels, with more precision at Master.
  return actions[
    cfrChoice(payoff, state.rng ^ Math.imul(main.id, 0x9e3779b9), level === 'expert' ? 64 : 256)
  ]!;
}

function choicesFor(cards: SupportCardId[]): CardChoice[] {
  const choices: CardChoice[] = [{ cardId: null }];
  for (const cardId of cards) {
    if (cardId === 'element:water')
      for (const role of ['pressure', 'protection', 'deception'] as const)
        choices.push({ cardId, role });
    else choices.push({ cardId });
  }
  return choices;
}

const indexOfMax = (values: number[]) =>
  values.reduce((best, value, index) => (value > values[best]! ? index : best), 0);

/** Deterministic regret matching approximates the hidden simultaneous-choice equilibrium. */
function cfrChoice(payoff: number[][], seed: number, iterations: number): number {
  const rows = payoff.length;
  const cols = payoff[0]?.length ?? 1;
  const rowRegret = Array(rows).fill(0) as number[];
  const colRegret = Array(cols).fill(0) as number[];
  const rowTotal = Array(rows).fill(0) as number[];
  const strategy = (regret: number[]) => {
    const positive = regret.map((value) => Math.max(0, value));
    const sum = positive.reduce((a, b) => a + b, 0);
    return sum > 0 ? positive.map((value) => value / sum) : positive.map(() => 1 / positive.length);
  };
  for (let iteration = 0; iteration < iterations; iteration++) {
    const a = strategy(rowRegret);
    const b = strategy(colRegret);
    a.forEach((value, i) => {
      rowTotal[i] = rowTotal[i]! + value;
    });
    const rowValues = payoff.map((row) => row.reduce((sum, value, j) => sum + value * b[j]!, 0));
    const currentRow = rowValues.reduce((sum, value, i) => sum + value * a[i]!, 0);
    const colValues = Array.from(
      { length: cols },
      (_, j) => -payoff.reduce((sum, row, i) => sum + row[j]! * a[i]!, 0),
    );
    const currentCol = colValues.reduce((sum, value, j) => sum + value * b[j]!, 0);
    rowValues.forEach((value, i) => {
      rowRegret[i] = rowRegret[i]! + value - currentRow;
    });
    colValues.forEach((value, j) => {
      colRegret[j] = colRegret[j]! + value - currentCol;
    });
  }
  let draw = createRng(seed).next() * rowTotal.reduce((a, b) => a + b, 0);
  for (let i = 0; i < rowTotal.length; i++) {
    draw -= rowTotal[i]!;
    if (draw <= 0) return i;
  }
  return rows - 1;
}
